import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { Button, Flex, Modal, Stack, UnstyledButton } from '@mantine/core';

import { usePostBoardInvitations } from 'api/boards';
import {
  CheckboxField,
  DescriptionField,
  EmailsListField,
  Form,
} from 'components/shared';
import { useShowNotification } from 'hooks';

type Props = {
  opened: boolean;
  onClose: () => void;
};

export const MemberInvitationModal = ({ opened, onClose }: Props) => {
  const { organisationId, boardId } = useParams() as {
    organisationId: string;
    boardId: string;
  };
  const form = useForm({
    defaultValues: {
      emails: [''],
      isAdminOrg: false,
      permissions: {
        canAddExternalComments: false,
        canCreateTemplate: false,
        canDeleteArchived: false,
        canFullTextEdit: false,
        canManageAllDocs: false,
        canManageManagers: false,
        canSeeOtherCompanies: false,
        canSeeOtherDocuments: false,
      },
    },
  });
  const [emailsFieldMode, setEmailsFieldMode] = useState<
    'multiple_inputs' | 'textarea'
  >('multiple_inputs');

  const { mutateAsync: postBoardInvitations } = usePostBoardInvitations();

  const showNotification = useShowNotification();
  const { t } = useTranslation('organisations');

  const onSubmit = async (data: any) => {
    try {
      await postBoardInvitations({
        path: { organisationId, boardId },
        body: data,
      });
      showNotification({
        variant: 'success',
        message: t('organisationView.boards.invitations.modal.successMessage'),
      });
      onClose();
      form.reset();
    } catch (error) {
      showNotification({
        variant: 'error',
        message: t('organisationView.boards.invitations.modal.errorMessage'),
      });
    }
  };

  const canManageAllDocsEnabled = form.watch('permissions.canManageAllDocs');

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      title={t('organisationView.boards.invitations.modal.title')}
    >
      <Form form={form} onSubmit={onSubmit}>
        <Stack py={24} sx={{ gap: 24 }}>
          <DescriptionField
            label={t(
              'organisationView.boards.invitations.modal.fields.emails.label',
            )}
            description={
              <Trans
                i18nKey={`organisations:organisationView.boards.invitations.modal.fields.emails.description.${
                  emailsFieldMode === 'multiple_inputs'
                    ? 'asMultipleInputs'
                    : 'asTextarea'
                }`}
                components={{
                  span: (
                    <UnstyledButton
                      onClick={() =>
                        setEmailsFieldMode(
                          emailsFieldMode === 'multiple_inputs'
                            ? 'textarea'
                            : 'multiple_inputs',
                        )
                      }
                      sx={theme => ({ color: theme.colors.violet[0] })}
                    />
                  ),
                }}
              />
            }
            error={undefined}
          >
            <EmailsListField name="emails" mode={emailsFieldMode} />
          </DescriptionField>
          <DescriptionField
            label={t(
              'organisationView.boards.invitations.modal.fields.isAdminOrg.label',
            )}
            description={t(
              'organisationView.boards.invitations.modal.fields.isAdminOrg.description',
            )}
            error={undefined}
          >
            <CheckboxField
              name="isAdminOrg"
              label={t(
                'organisationView.boards.invitations.modal.fields.isAdminOrg.checkboxLabel',
              )}
            />
          </DescriptionField>
          <DescriptionField
            label={t(
              'organisationView.boards.invitations.modal.fields.permissions.label',
            )}
            description={t(
              'organisationView.boards.invitations.modal.fields.permissions.description',
            )}
            error={undefined}
          >
            <CheckboxField
              name="permissions.canFullTextEdit"
              label={t('boardPermissions.canFullTextEdit')}
            />
            <CheckboxField
              name="permissions.canCreateTemplate"
              label={t('boardPermissions.canCreateTemplate')}
            />
            <CheckboxField
              {...(canManageAllDocsEnabled ? { checked: true } : {})}
              disabled={canManageAllDocsEnabled}
              name="permissions.canSeeOtherDocuments"
              label={t('boardPermissions.canSeeOtherDocuments')}
            />
            <CheckboxField
              name="permissions.canSeeOtherCompanies"
              label={t('boardPermissions.canSeeOtherCompanies')}
            />
            <CheckboxField
              name="permissions.canDeleteArchived"
              label={t('boardPermissions.canDeleteArchived')}
            />
            <CheckboxField
              {...(canManageAllDocsEnabled ? { checked: true } : {})}
              disabled={canManageAllDocsEnabled}
              name="permissions.canAddExternalComments"
              label={t('boardPermissions.canAddExternalComments')}
            />
            <CheckboxField
              {...(canManageAllDocsEnabled ? { checked: true } : {})}
              disabled={canManageAllDocsEnabled}
              name="permissions.canManageManagers"
              label={t('boardPermissions.canManageManagers')}
            />
            <CheckboxField
              name="permissions.canManageAllDocs"
              label={t('boardPermissions.canManageAllDocs')}
            />
          </DescriptionField>
        </Stack>
        <Flex justify="flex-end" gap={16}>
          <Button variant="default" onClick={onClose}>
            {t('organisationView.boards.invitations.modal.cancelButton')}
          </Button>
          <Button
            color="green"
            type="submit"
            loading={form.formState.isSubmitting}
          >
            {t('organisationView.boards.invitations.modal.sendButton')}
          </Button>
        </Flex>
      </Form>
    </Modal>
  );
};
